import { useEffect, useCallback } from 'react'
import { useSellerQueueContext } from '../contexts/SellerQueueContext'
import * as sellerQueueService from '../services/sellerQueueService'
import { useSeller } from './useSeller'

/**
 * Hook: useSellerQueue
 * Manages seller queue state (entering, leaving, pausing, resuming queue)
 * Auto-refetches queue status periodically
 */
export const useSellerQueue = () => {
  const {
    queueEntry,
    isLoadingQueue,
    errorQueue,
    setQueueEntry,
    setIsLoadingQueue,
    setErrorQueue,
    setLastUpdated,
  } = useSellerQueueContext()

  const { getTokenSeller } = useSeller()

  const tokenSeller = getTokenSeller()
  const sellerId = tokenSeller?.id
  const pluginspaceId = tokenSeller?.pluginspaceId

  /**
   * Fetch current queue entry for seller
   */
  const refetchQueue = useCallback(async () => {
    if (!sellerId || !pluginspaceId) return

    try {
      setIsLoadingQueue(true)
      setErrorQueue(null)
      const queueList = await sellerQueueService.getQueueByPluginspace(pluginspaceId)
      
      // Find current seller's entry
      const entry = queueList.find((q) => q.sellerId === sellerId)
      setQueueEntry(entry || null)
      setLastUpdated(new Date())
    } catch (error) {
      setErrorQueue(error instanceof Error ? error : new Error(String(error)))
      setQueueEntry(null)
    } finally {
      setIsLoadingQueue(false)
    }
  }, [sellerId, pluginspaceId, setQueueEntry, setIsLoadingQueue, setErrorQueue, setLastUpdated])

  /**
   * Enter queue (seller becomes available)
   * Status: offline → available
   */
  const enterQueue = useCallback(async () => {
    if (!sellerId || !pluginspaceId) {
      throw new Error('Seller ID or Pluginspace ID missing')
    }

    try {
      setIsLoadingQueue(true)
      setErrorQueue(null)
      const newEntry = await sellerQueueService.createQueueEntry(pluginspaceId, sellerId)
      setQueueEntry(newEntry)
      setLastUpdated(new Date())
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error))
      setErrorQueue(err)
      throw err
    } finally {
      setIsLoadingQueue(false)
    }
  }, [sellerId, pluginspaceId, setQueueEntry, setIsLoadingQueue, setErrorQueue, setLastUpdated])

  /**
   * Pause queue (seller is available but temporarily not accepting customers)
   * Status: available → paused
   */
  const pauseQueue = useCallback(async () => {
    if (!queueEntry?.id) {
      throw new Error('No active queue entry')
    }

    try {
      setIsLoadingQueue(true)
      setErrorQueue(null)
      await sellerQueueService.updateQueueStatus(queueEntry.id, 'paused')
      setQueueEntry({ ...queueEntry, status: 'paused' })
      setLastUpdated(new Date())
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error))
      setErrorQueue(err)
      throw err
    } finally {
      setIsLoadingQueue(false)
    }
  }, [queueEntry, setQueueEntry, setIsLoadingQueue, setErrorQueue, setLastUpdated])

  /**
   * Resume queue (seller becomes available again)
   * Status: paused → available
   */
  const resumeQueue = useCallback(async () => {
    if (!queueEntry?.id) {
      throw new Error('No active queue entry')
    }

    try {
      setIsLoadingQueue(true)
      setErrorQueue(null)
      await sellerQueueService.updateQueueStatus(queueEntry.id, 'available')
      setQueueEntry({ ...queueEntry, status: 'available' })
      setLastUpdated(new Date())
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error))
      setErrorQueue(err)
      throw err
    } finally {
      setIsLoadingQueue(false)
    }
  }, [queueEntry, setQueueEntry, setIsLoadingQueue, setErrorQueue, setLastUpdated])

  /**
   * Leave queue (seller goes offline)
   * Status: available/paused → offline
   */
  const leaveQueue = useCallback(async () => {
    if (!sellerId) {
      throw new Error('Seller ID missing')
    }

    try {
      setIsLoadingQueue(true)
      setErrorQueue(null)
      await sellerQueueService.removeFromQueue(sellerId)
      setQueueEntry(null)
      setLastUpdated(new Date())
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error))
      setErrorQueue(err)
      throw err
    } finally {
      setIsLoadingQueue(false)
    }
  }, [sellerId, setQueueEntry, setIsLoadingQueue, setErrorQueue, setLastUpdated])

  /**
   * Auto-refetch queue status every 10 seconds if in queue
   * Also perform initial fetch when sellerId/pluginspaceId are available
   */
  useEffect(() => {
    if (!sellerId || !pluginspaceId) return

    // Initial fetch when component mounts
    refetchQueue()

    // Auto-refetch every 10 seconds
    const interval = setInterval(refetchQueue, 10000)
    return () => clearInterval(interval)
  }, [sellerId, pluginspaceId, refetchQueue])

  return {
    queueEntry,
    isLoading: isLoadingQueue,
    error: errorQueue,
    enterQueue,
    pauseQueue,
    resumeQueue,
    leaveQueue,
    refetchQueue,
  }
}
